/**
 * Domain Detection Utility
 * Detects which organization is being accessed based on the domain
 */

/**
 * Get the current domain from the browser
 * @returns {string} - Current domain (e.g., "gatortaxform.com" or "natalietaxform.com")
 */
export const getCurrentDomain = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  // Get hostname (removes port if present)
  let hostname = window.location.hostname;
  
  // For local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }
  
  // Remove 'www.' prefix if present
  if (hostname.startsWith('www.')) {
    hostname = hostname.substring(4);
  }
  
  return hostname;
};

/**
 * Get the current subdomain from the browser
 * @returns {string|null} - Subdomain (e.g., "natalie" from "natalie.gatortaxform.com")
 */
export const getCurrentSubdomain = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  const hostname = window.location.hostname;
  
  // For local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  
  // Split by dots
  const parts = hostname.split('.');
  
  // If we have more than 2 parts (e.g., natalie.gatortaxform.com)
  // Return the first part as subdomain
  if (parts.length > 2) {
    const potentialSubdomain = parts[0];
    // Ignore 'www' as it's not a real subdomain
    if (potentialSubdomain === 'www') {
      return null;
    }
    return potentialSubdomain;
  }
  
  return null;
};

/**
 * Check if we're in local development mode
 * @returns {boolean}
 */
export const isLocalDevelopment = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

/**
 * Get organization query parameters for Supabase
 * This helps determine which organization to load based on domain/subdomain
 * @returns {object} - Query object for finding organization
 */
export const getOrganizationQuery = () => {
  const domain = getCurrentDomain();
  const subdomain = getCurrentSubdomain();
  
  // For local development, default to gatortaxform.com
  if (isLocalDevelopment()) {
    return {
      field: 'custom_domain',
      value: 'gatortaxform.com'
    };
  }
  
  // If we have a subdomain, search by that
  if (subdomain) {
    return {
      field: 'subdomain',
      value: subdomain
    };
  }
  
  // Otherwise search by custom domain
  return {
    field: 'custom_domain',
    value: domain
  };
};
